// Pure helpers for the slice-B1 checklist click-to-focus behavior.
//
// The capture checklist (in-lead `ChecklistCard` + dock-level
// `ActivityDockActiveLeadChecklistSection`) shares a four-row model in
// `leadChecklistItems.ts`. When the user clicks a "missing" row we want
// to:
//   1. Open the right accordion (Product / Location & Source) if closed.
//   2. Scroll the underlying input into view.
//   3. Focus the input after the scroll animation kicks off.
//
// This module owns the pure pieces of that:
//   - the `LeadChecklistItemKey → DOM selector` mapping
//   - the scroll-into-view + delayed-focus helper
//
// The accordion-flip + multi-step orchestration lives in
// `useChecklistFieldFocus` because it needs React setters.
//
// Slice B1 of the v1.0 Lead Control MVP polish pass — see
// `docs/planning/lead_control_mvp_polish.md`.

import type { LeadChecklistItemKey } from "@/components/lead/sections/leadChecklistItems";

/**
 * Default delay between `scrollIntoView` kicking off and the focus call.
 * Matches the existing `ProductCard.focusNotes` pattern: long enough that
 * the caret doesn't fight the smooth-scroll animation, short enough that
 * the user doesn't notice a pause.
 */
export const LEAD_FIELD_FOCUS_DELAY_MS = 250;

/**
 * DOM attribute used to tag the four target inputs. Decoupled from
 * input `id` attrs because the same input can render in multiple
 * places (mobile vs desktop layouts, mirror views) and we want a stable
 * selector that doesn't fight with React-generated ids.
 */
export const LEAD_FIELD_DATA_ATTR = "data-lead-field";

/**
 * Field-name -> selector map. Picks the *primary* missing input per
 * checklist key:
 *   - contact_info → phone (name might be filled even when phone is the
 *     missing piece; phone is the harder signal).
 *   - product_line, lead_source, budget → the canonical input.
 *
 * String values are the raw `data-lead-field` slugs; the actual selector
 * is built by `selectorForChecklistKey`.
 */
export const LEAD_CHECKLIST_FIELD_TARGETS: Record<LeadChecklistItemKey, string> = {
  contact_info: "phone",
  product_line: "product_line",
  lead_source: "lead_source_category",
  budget: "enunciated_budget",
};

/**
 * Returns the CSS selector for the input that backs a given checklist
 * key. Stable across re-renders since both the attribute name and the
 * mapping are constants.
 */
export function selectorForChecklistKey(key: LeadChecklistItemKey): string {
  const slug = LEAD_CHECKLIST_FIELD_TARGETS[key];
  return `[${LEAD_FIELD_DATA_ATTR}="${slug}"]`;
}

/**
 * Returns whether a checklist key's target input lives inside the
 * Product details accordion. Used by the focus orchestrator to decide
 * which `setShowXxxDetails(true)` setter to flip.
 */
export function isProductAccordionKey(key: LeadChecklistItemKey): boolean {
  return key === "product_line" || key === "budget";
}

/**
 * Returns whether a checklist key's target input lives inside the
 * Location & Source details accordion.
 */
export function isLocationAccordionKey(key: LeadChecklistItemKey): boolean {
  return key === "lead_source";
}

export type ScrollIntoViewAndFocusOptions = {
  /** ms to wait between scroll and focus. Defaults to LEAD_FIELD_FOCUS_DELAY_MS. */
  delayMs?: number;
  /** Optional document root override — exposed for tests. */
  doc?: Document;
};

/**
 * Promotes the `ProductCard.focusNotes` pattern to a reusable helper:
 * smooth-scrolls the target into view, then focuses it after a short
 * delay so the caret doesn't race the scroll. No-ops cleanly when the
 * selector finds nothing or the env isn't a browser. Returns whether
 * the lookup succeeded so callers can decide whether to retry.
 */
export function scrollIntoViewAndFocus(
  selector: string,
  options: ScrollIntoViewAndFocusOptions = {},
): boolean {
  const doc = options.doc ?? (typeof document !== "undefined" ? document : null);
  if (!doc) return false;
  const target = doc.querySelector<HTMLElement>(selector);
  if (!target) return false;
  target.scrollIntoView({ behavior: "smooth", block: "center" });
  const delay = options.delayMs ?? LEAD_FIELD_FOCUS_DELAY_MS;
  if (typeof window !== "undefined") {
    window.setTimeout(() => target.focus(), delay);
  } else {
    target.focus();
  }
  return true;
}
